<template>
  <footer class="container">
    <ul class="links feedback">
      <CoinListButton />
    </ul>

    <ul class="social-icons">
      <li v-for="link in socialLinks" :key="link.url">
        <a
          :href="link.url"
          :title="link.label"
          target="_blank"
          rel="noreferrer noopener"
        >
          <img :src="link.icon" />
        </a>
      </li>
    </ul>

    <ul v-for="(links, index) in linksList" :key="index" class="links">
      <li v-for="link in links" :key="link.url">
        <a :href="link.url" rel="noreferrer noopenner">
          {{ link.label }}
        </a>
      </li>
    </ul>

    <p class="copyright">
      &copy; {{ year }} {{ $t('footer.copyright') }} <wbr /><router-link
        to="/terms"
        >{{ $t('footer.termsOfUse') }}</router-link
      >. {{ $t('footer.version') }} {{ chain }}{{ version }}.
    </p>
  </footer>
</template>

<script setup lang="ts">
import {
  APP_VERSION,
  BUILD_CHAIN_ID,
  ChainsEnum,
  CORRECT_CHAIN,
  MOVEMENT_MAINNET_CHAIN_ID,
} from '@/constants';
import { CoinListButton } from '@/components/UI/CoinListButton';
import { computed } from 'vue-demi';
import discordSvg from '@/assets/social/discord.svg';
import telegramSvg from '@/assets/social/telegram.svg';
import twitterSvg from '@/assets/social/twitter.svg';
import githubSvg from '@/assets/social/github.svg';
import { useI18n } from '@/composables/useI18n';

const { t } = useI18n();

const isMovement = computed(() => BUILD_CHAIN_ID === MOVEMENT_MAINNET_CHAIN_ID);

const linksList = [
  [
    {
      label: t('footer.pontemNetwork'),
      url: 'https://pontem.network/',
    },
    {
      label: t('footer.dex'),
      url: 'https://pontem.network/liquidswap',
    },
    {
      label: t('footer.move'),
      url: 'https://pontem.network/move-code-playground',
    },
  ],
  [
    {
      label: t('footer.resourceCenter'),
      url: 'https://pontem.network/blog',
    },
    {
      label: t('footer.documentation'),
      url: 'https://docs.liquidswap.com',
    },
    {
      label: t('footer.getStarted'),
      url: 'https://pontem.network/get-started',
    },
  ],
  [
    !isMovement.value && {
      label: t('footer.ambassadorProgram'),
      url: 'https://pontem.network/ambassador-program',
    },
    {
      label: t('footer.privacyPolicy'),
      url: 'https://pontem.network/privacypolicy',
    },
  ].filter(Boolean),
];

const socialLinks = [
  {
    icon: discordSvg,
    label: 'Discord',
    url: 'https://discord.com/invite/44QgPFHYqs',
  },
  {
    icon: telegramSvg,
    label: 'Telegram',
    url: 'https://t.me/pontemnetworkchat',
  },
  {
    icon: twitterSvg,
    label: 'Twitter',
    url: 'https://twitter.com/PontemNetwork',
  },
  {
    icon: githubSvg,
    label: 'Github',
    url: 'https://github.com/pontem-network',
  },
];

const year = new Date().getFullYear();

const version = computed(() => APP_VERSION);

const chain = computed(() =>
  CORRECT_CHAIN.id === ChainsEnum['lumio-testnet'] ? 't' : '',
);
</script>
<style lang="scss" scoped>
.container {
  margin-top: 56px;

  @media (min-width: 1024px) {
    display: flex;
    flex-wrap: wrap;
    column-gap: 24px;
  }

  @media (min-width: 1440px) {
    justify-content: center;
  }
}

.social-icons {
  list-style-type: none;
  display: flex;
  flex-direction: row;
  padding: 0;
  margin: 0;
  align-items: center;
  justify-content: center;
  gap: 24px;
  order: 2;

  @media (max-width: 1024px) {
    margin-bottom: 32px;
  }

  @media (min-width: 640px) {
    gap: 40px;
  }

  @media (min-width: 1024px) {
    width: 100px;
    flex-wrap: wrap;
    row-gap: 10px;
    column-gap: 45px;
    align-items: flex-start;
  }

  @media (min-width: 1440px) {
    flex-direction: column;
    position: fixed;
    top: 50%;
    right: 0;
    padding-right: 33px;
    transform: translate(0%, -50%);
    gap: 40px;
    width: initial;

    img {
      color: white;
      width: 20px;
      cursor: pointer;
      opacity: 0.6;
      transition: 0.25s;
      &:hover {
        opacity: 1;
      }
    }
  }
}

.links {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  margin: 0;
  list-style-type: none;
  gap: 16px;
  margin-bottom: 32px;
  order: 1;

  @media (min-width: 1024px) {
    width: 215px;
    align-items: flex-start;
    border-right: 1px solid hsla(243, 69%, 94%, 0.2);
  }

  @media (min-width: 1440px) {
    flex-direction: row;
    width: initial;
    border: 0;
    margin-bottom: 16px;
  }

  a {
    font-weight: 500;
    font-size: 15px;
    line-height: 18px;
    color: rgba(#fbfaff, 0.6);

    &:hover,
    &:focus,
    &:focus-visible {
      color: var(--primary-color-text);
    }
  }
}

.feedback {
  @media (min-width: 1024px) {
    margin-bottom: 0;
    width: 0;
    border: 0;
  }
}

.copyright {
  max-width: 318px;
  margin: 0 auto;
  padding: 0 24px 24px;
  text-align: center;
  order: 3;

  font-weight: 400;
  font-size: 11px;
  line-height: 16px;
  color: rgba(#fbfaff, 0.5);

  @media (min-width: 640px) {
    min-width: 100%;
  }

  @media (min-width: 1024px) {
    margin: 0;
    text-align: start;
  }

  @media (min-width: 1440px) {
    margin: 0 auto;
    text-align: center;
  }

  a {
    color: rgba(#fbfaff, 0.5);

    &:visited {
      color: rgba(#fbfaff, 0.5);
    }
    &:hover {
      color: #ffffff;
    }
  }
}
</style>
